import {Component, inject, linkedSignal, signal} from '@angular/core';
import {ButtonComponent, InputComponent, InputContainerComponent} from "@components";
import {OptionType, ToggleComponent} from "../../../../../components/forms/toggle/toggle.component";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MediaService, SlideService} from "@api-services";
import {FileUploaderComponent} from "../../../../../components/forms/file-uploader/file-uploader.component";
import {map, switchMap, tap} from "rxjs/operators";
import {rxResource} from "@angular/core/rxjs-interop";
import {CreateMediaRequestDto, MediaDto, SlideDto} from "@api-contracts";
import {CheckboxComponent} from "../../../../../components/forms/checkbox/checkbox.component";
import {forkJoin, of} from "rxjs";
import {watchParam} from "@utils";
import {Router} from "@angular/router";

@Component({
  selector: 'wun-create-form',
  standalone: true,
  imports: [
    InputComponent,
    InputContainerComponent,
    ToggleComponent,
    ReactiveFormsModule,
    ButtonComponent,
    FileUploaderComponent,
    CheckboxComponent
  ],
  templateUrl: './create-form.component.html',
  styleUrl: './create-form.component.scss'
})
export class CreateFormComponent {

  private readonly slideService = inject(SlideService);
  private readonly mediaService = inject(MediaService);
  private readonly router = inject(Router);

  sliderOptions: OptionType[] = [
    {
      value: true,
      label: 'Visibile',
    },
    {
      value: false,
      label: 'Nascosto',
    }
  ];

  slideForm = new FormGroup({
    name: new FormControl<string>('', Validators.required),
    visible: new FormControl<boolean>(true, Validators.required),
  })

  routeId = watchParam("id")

  id = linkedSignal(() => {
    const _ = this.routeId();
    return _ ? Number(_) : undefined;
  })

  slide = rxResource({
    params: this.id,
    stream: (p) => {
      if (p.params)
        return this.slideService.slidesGetById({
          id: p.params
        }).pipe(
          tap(slide => {
            this.slideForm.controls.name.setValue((slide.data.name as any)['it-IT'])
            this.slideForm.controls.visible.setValue(slide.data.visible);
          })
        );
      return of(null)
    }
  })

  _medias = rxResource({
    params: this.id,
    stream: (params) => {
      const id = params.params;
      if (!id) return of([]);
      return this.mediaService.mediaFindAndPaginate({
        filters: `{"slide": ${id}, "fileScaling": "DEFAULT"}`
      }).pipe(map(f => f.data))
    }
  })

  medias = linkedSignal<(MediaDto & {file?: File | undefined})[]>(() => {
    const _ = this._medias.value() || [];
    return [
      ..._,
    ]
  })

  save() {
    const id = this.id();
    if (id)
      this.slideService.slidesUpdate({
        id,
        patchSlideRequestDto: {
          name: {
            'it-IT': this.slideForm.value.name,
          },
          visible: this.slideForm.controls.visible.value!
        }
      })
        .pipe(
          switchMap(
            d => this
              .uploadImages(d.data)
          )
        )
        .subscribe()
    else
      this.slideService.slidesCreate({
        createSlideRequestDto: {
          name: {
            'it-IT': this.slideForm.value.name,
          },
          visible: this.slideForm.controls.visible.value!
        }
      })
        .pipe(
          switchMap(d => {
            return this
              .uploadImages(d.data)
              .pipe(
                map(f => d)
              )
          }))
        .subscribe(f => {
          this.router.navigate(['backoffice/editorials/slides', f.data.id])
        })
  }

  uploadImages(slide: SlideDto) {
    return forkJoin(
      this.medias()
        .map(f => this.mediaService.mediaUpload({
          ...f,
          file: f.file!,
          url: '',
          slide: slide.id
        } as any)))
  }

  fileSelected($event: (CreateMediaRequestDto & { file?: File }) | undefined) {
    const m = this.medias();
    m.push({
      ...$event!,
      id: 0
    } as MediaDto);
    this.medias.set(m)
  }

  deleteMedia(media: MediaDto) {
    const _ = confirm('Attenzione l\operazione non é reversibile');
    if (_)
      this.mediaService.mediaDelete({
        id: media.id
      }).subscribe(d => {
        this.medias.update((medias) => {
          return medias.filter(f => f.id !== media.id);
        })
      })
  }
}
